import type { UIMessage } from 'ai';

/**
 * What a chat send carries as `messages`: the newest message only.
 *
 * The AI SDK's default transport posts the whole in-memory chat on every send,
 * tool results included. The server never needs it: it rebuilds the history
 * from the stored rows (the chat route's DB rebuild), exactly as it already
 * does for its own one-message continuation turns. Shipping the whole chat
 * only cost an upload that grows with every tool result, and once a single
 * large result pushed it past the server's request-body limit, every send in
 * that chat was refused and the chat could not be continued at all.
 */
export function newestMessageOnly<M extends UIMessage>(messages: readonly M[]): M[] {
  const last = messages[messages.length - 1];
  return last ? [last] : [];
}

/**
 * The transport's `prepareSendMessagesRequest`: the same body the SDK would
 * build by default (our `body()` fields plus id, trigger and messageId), with
 * `messages` cut down to {@link newestMessageOnly}.
 */
export function buildChatSendBody(options: {
  id: string;
  messages: readonly UIMessage[];
  body: Record<string, unknown> | undefined;
  trigger: string;
  messageId: string | undefined;
}): Record<string, unknown> {
  return {
    ...(options.body ?? {}),
    id: options.id,
    messages: newestMessageOnly(options.messages),
    trigger: options.trigger,
    messageId: options.messageId,
  };
}
